import moment from "moment";
import React, { useState } from "react";
import { DicomViewer } from "../utilities/dicom-viewer";
import ReportViewer from "../components/ReportViewer";
import { useParams } from "react-router-dom";
import {
	xhrClinicalGetBundledExamsByExamId,
	xhrClinicalGetExamStudyByExamId,
	xhrClinicalSearchExam,
} from "../utilities/xhr-clinical";

const DicomViewerShare = () => {
	const { id, token } = useParams();
	const [tid, setTid] = useState(0);
	const [reportId, setReportId] = useState(0);
	const [studyUIds, setStudyUIds] = useState([]);
	const [isCompactLayout, setIsCompactLayout] = useState(() =>
		typeof window !== "undefined" ? window.innerWidth < 992 : false
	);
	const [compactView, setCompactView] = useState("report");

	const getUniqueStudyUids = (studyValues) =>
		[...new Set((studyValues ?? []).map((x) => String(x ?? "").trim()).filter(Boolean))];

	const getUniqueExamIds = (examValues) =>
		[...new Set((examValues ?? []).map((x) => parseInt(x, 10)).filter(Boolean))];

	const getAssociatedExamSearchRange = () => {
		const endDate = moment().utc().endOf("day").format("YYYY-MM-DD hh:mm:ss");
		const startDate = moment()
			.utc()
			.endOf("day")
			.subtract(11, "months")
			.startOf("day")
			.format("YYYY-MM-DD hh:mm:ss");

		return {
			sdt: startDate,
			edt: endDate,
		};
	};

	const loadData = React.useCallback(async (id, token) => {
		try {
			const encodedId = decodeURIComponent(id || "");
			const decoded = atob(encodedId);
			const parts = String(decoded || "").split("|");

			const tidStr = parts[0] ?? "0";
			const studyStr = parts[1] ?? "";
			const reportStr = parts[2] ?? "0";
			const parsedTid = parseInt(tidStr, 10) || 0;
			setTid(parsedTid);
			const frozenStudyUids = getUniqueStudyUids(studyStr ? studyStr.split(";") : []);

			var acnStr = "";
			let resolvedReportId = parseInt(reportStr, 10) || 0;
			let resolvedStudyUids = [...frozenStudyUids];
			let matchedExams = [];
			if (parts.length > 3) {
				acnStr = parts[3] ?? "-";
				let paramObj = {
					pr: 200,
					pi: 0,
					si: acnStr,
					...getAssociatedExamSearchRange(),
				};
				const searchData = await xhrClinicalSearchExam(token, paramObj);
				matchedExams = searchData?.result?.data ?? [];
				resolvedReportId = matchedExams?.[0]?.reportId ?? resolvedReportId;
				console.info("share, fetch reportid for acn:" + resolvedReportId);
			} else {
				if (resolvedReportId > 0) {
					let paramObj = {
						pr: 200,
						pi: 0,
						si: String(resolvedReportId),
						...getAssociatedExamSearchRange(),
					};
					const searchData = await xhrClinicalSearchExam(token, paramObj);
					matchedExams = searchData?.result?.data ?? [];
				}
			}

			if (matchedExams.length > 0) {
				const bundledExamResponses = await Promise.all(
					matchedExams
						.map((exam) => exam?.id)
						.filter(Boolean)
						.map((examId) => xhrClinicalGetBundledExamsByExamId(token, examId))
				);

				const bundledExamIds = getUniqueExamIds([
					...matchedExams.map((exam) => exam?.id),
					...bundledExamResponses.flatMap((response) =>
						(response?.result?.data ?? []).map((exam) => exam?.id)
					),
				]);

				const studyResponses = await Promise.all(
					bundledExamIds
						.map((examId) => xhrClinicalGetExamStudyByExamId(token, examId))
				);

				const liveStudyUids = studyResponses
					.flatMap((response) => response?.result?.data ?? [])
					.map((study) => study?.studyUid);

				resolvedStudyUids = getUniqueStudyUids([...frozenStudyUids, ...liveStudyUids]);
			}

			setReportId(resolvedReportId);
			setStudyUIds(resolvedStudyUids);
		} catch (e) {
		}
	}, []);

	React.useEffect(() => {
		loadData(id, token);
	}, [id, token, loadData]);

	React.useEffect(() => {
		const handleResize = () => {
			setIsCompactLayout(window.innerWidth < 992);
		};

		handleResize();
		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	const viewerMode =
		new URLSearchParams(window.location.search).get("viewer") ?? "ohif-v3";
	const hasReport = reportId > 0;
	const hasImages = studyUIds.length > 0;
	const showCompactSelector = isCompactLayout && hasReport && hasImages;

	React.useEffect(() => {
		if (!showCompactSelector) {
			if (hasReport) {
				setCompactView("report");
			} else if (hasImages) {
				setCompactView("images");
			}
			return;
		}

		setCompactView((currentView) => {
			if (currentView === "report" && hasReport) return currentView;
			if (currentView === "images" && hasImages) return currentView;
			if (hasReport) return "report";
			if (hasImages) return "images";
			return currentView;
		});
	}, [showCompactSelector, hasReport, hasImages]);

	const buttonBaseStyle = {
		width: "40px",
		height: "40px",
		borderRadius: "8px",
		border: "2px solid #e78e4b",
		background: "#ffffff",
		color: "#05585E",
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		cursor: "pointer",
		boxShadow: "0 4px 10px rgba(0,0,0,0.18)",
	};

	return (
		<div
			style={{
				display: "flex",
				height: "100%",
				flexDirection: showCompactSelector ? "column" : "row",
				position: "relative",
				background: "#f5f5f5",
			}}
		>
			{showCompactSelector && (
				<div
					style={{
						position: "absolute",
						zIndex: 20,
						display: "flex",
						gap: "8px",
						padding: "8px",
						bottom: "0",
						borderRadius: "0px 10px 0px 0px",
						background: "rgba(255,255,255,0.92)",
						boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
					}}
				>
					<button
						type="button"
						onClick={() => setCompactView("images")}
						title="Images"
						style={{
							...buttonBaseStyle,
							background: compactView === "images" ? "#e78e4b" : "#ffffff",
						}}
					>
						<i
							className="zmdi zmdi-eye zmdi-hc-lg"
							style={{ color: compactView === "images" ? "#ffffff" : "#05585E" }}
						/>
					</button>
					<button
						type="button"
						onClick={() => setCompactView("report")}
						title="Report"
						style={{
							...buttonBaseStyle,
							background: compactView === "report" ? "#e78e4b" : "#ffffff",
						}}
					>
						<i
							className="zmdi zmdi-file zmdi-hc-lg"
							style={{ color: compactView === "report" ? "#ffffff" : "#05585E" }}
						/>
					</button>
				</div>
			)}

			{hasReport && (!showCompactSelector || compactView === "report") && (
				<div style={{ flex: "1", minWidth: 0 }}>
					<ReportViewer reportId={reportId} outerToken={token} />
				</div>
			)}

			{hasImages && (!showCompactSelector || compactView === "images") && (
				<div style={{ flex: "1", minWidth: 0 }}>
					<DicomViewer
						mode={viewerMode}
						tenantId={tid}
						studyUids={studyUIds}
						modality=""
						showMainImages={true}
						outerToken={token}
					/>
				</div>
			)}
		</div>
	);
};

export default DicomViewerShare;
